import React, { createContext, useCallback, useMemo, useState } from 'react';
import axios from 'axios';

import { API_BASE_URL } from '../config/apiBase';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const EventContext = createContext();

export const EventProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [subEventsByEvent, setSubEventsByEvent] = useState({});
  const [loading, setLoading] = useState(false);

  const handleError = (error) => {
    console.error('Erro em EventContext:', error);
    const message = error?.response?.data?.message || 'Erro inesperado ao processar requisição.';
    throw new Error(message);
  };

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/events`, {
        headers: getAuthHeaders(),
      });
      setEvents(response.data || []);
      return response.data || [];
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const createEvent = useCallback(async (payload) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/events`, payload, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      setEvents((prev) => [response.data, ...prev]);
      return response.data;
    } catch (error) {
      handleError(error);
    }
  }, []);

  const updateEvent = useCallback(async (id, payload) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/events/${id}`, payload, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      setEvents((prev) => prev.map((event) => (event.id === id ? response.data : event)));
      return response.data;
    } catch (error) {
      handleError(error);
    }
  }, []);

  const deleteEvent = useCallback(async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/events/${id}`, {
        headers: getAuthHeaders(),
      });
      setEvents((prev) => prev.filter((event) => event.id !== id));
      setSubEventsByEvent((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    } catch (error) {
      handleError(error);
    }
  }, []);

  const fetchSubEvents = useCallback(async (eventId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/events/${eventId}/sub-events`, {
        headers: getAuthHeaders(),
      });
      setSubEventsByEvent((prev) => ({
        ...prev,
        [eventId]: response.data || [],
      }));
      return response.data || [];
    } catch (error) {
      handleError(error);
    }
  }, []);

  const createSubEvent = useCallback(async (eventId, payload) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/events/${eventId}/sub-events`, payload, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      setSubEventsByEvent((prev) => ({
        ...prev,
        [eventId]: [response.data, ...(prev[eventId] || [])],
      }));
      return response.data;
    } catch (error) {
      handleError(error);
    }
  }, []);

  const updateSubEvent = useCallback(async (subEventId, payload) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/events/sub-events/${subEventId}`, payload, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      setSubEventsByEvent((prev) => {
        const copy = { ...prev };
        Object.keys(copy).forEach((eventId) => {
          copy[eventId] = copy[eventId]?.map((item) => (item.id === subEventId ? response.data : item));
        });
        return copy;
      });

      return response.data;
    } catch (error) {
      handleError(error);
    }
  }, []);

  const deleteSubEvent = useCallback(async (eventId, subEventId) => {
    try {
      await axios.delete(`${API_BASE_URL}/events/sub-events/${subEventId}`, {
        headers: getAuthHeaders(),
      });
      setSubEventsByEvent((prev) => ({
        ...prev,
        [eventId]: (prev[eventId] || []).filter((item) => item.id !== subEventId),
      }));
    } catch (error) {
      handleError(error);
    }
  }, []);

  const downloadReport = useCallback(async ({ eventId, subEventId, format = 'csv' }) => {
    try {
      const params = new URLSearchParams({ format });
      if (subEventId) {
        params.append('subEventId', subEventId);
      }
      const response = await axios.get(`${API_BASE_URL}/events/${eventId}/report?${params.toString()}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      handleError(error);
    }
  }, []);

  const value = useMemo(
    () => ({
      events,
      subEventsByEvent,
      loading,
      fetchEvents,
      createEvent,
      updateEvent,
      deleteEvent,
      fetchSubEvents,
      createSubEvent,
      updateSubEvent,
      deleteSubEvent,
      downloadReport,
    }),
    [
      events,
      subEventsByEvent,
      loading,
      fetchEvents,
      createEvent,
      updateEvent,
      deleteEvent,
      fetchSubEvents,
      createSubEvent,
      updateSubEvent,
      deleteSubEvent,
      downloadReport,
    ]
  );

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
};
